"use client";

import { useEffect, useRef, useState } from "react";
import { formatAmount, type TokenScale } from "@/lib/format/amount";

/* A balance that rolls to its new figure instead of jumping — the whitelabel's
   useAnimatedNumber, 800ms of cubic ease-out on a requestAnimationFrame, and
   the reason its header feels live while a swapped string feels like a reload.

   The animation is presentation only: interpolation happens on a float, but
   the resting frame renders the exact formatted amount from the base-unit
   string, so nothing the player settles on ever came through floating point.
   Reduced motion snaps straight to the figure. */

const DURATION_MS = 800;

const asNumber = (amount: string, token: TokenScale) =>
  Number.parseFloat(formatAmount(amount, token, { grouped: false })) || 0;

export function AnimatedAmount({
  amount,
  token,
}: {
  amount: string;
  token: TokenScale;
}) {
  const target = asNumber(amount, token);
  const [display, setDisplay] = useState<number | null>(null);
  const previous = useRef(target);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    const start = previous.current;
    previous.current = target;

    if (start === target) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(null);
      return;
    }

    const startedAt = performance.now();

    const tick = (nowMs: number) => {
      const progress = Math.min((nowMs - startedAt) / DURATION_MS, 1);
      const eased = 1 - (1 - progress) ** 3;

      if (progress < 1) {
        setDisplay(start + (target - start) * eased);
        frame.current = requestAnimationFrame(tick);
      } else {
        /* Land on the true string, not the interpolated float. */
        setDisplay(null);
      }
    };

    frame.current = requestAnimationFrame(tick);

    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    };
  }, [target]);

  return (
    <>
      {display === null
        ? formatAmount(amount, token)
        : display.toLocaleString("en-US", {
            minimumFractionDigits: Math.min(token.significantDecimals, 2),
            maximumFractionDigits: Math.min(token.significantDecimals, 2),
          })}
    </>
  );
}
